import React, { useState } from "react";
import { gql, useQuery } from "@apollo/client";

import { Error } from "src/components/error/error";
import { Loading } from "src/components/loading/loading";
import { Viewer } from "src/generated/types";
import { Separator } from "src/components/separator/separator";
import { ReportGrid } from "src/components/report_grid/report_grid";
import { Choice, Select } from "src/components/select/select";

/*
 * GraphQL
 */

export const LIST_REPORTS_QUERY = gql`
    query ListReports {
        viewer {
            user {
                id
                school {
                    id
                    name
                    reports(where: { results: { exists: true } }, order: [year_DESC]) {
                        count
                        edges {
                            node {
                                id
                                year
                                type
                            }
                        }
                    }
                }
            }
        }
    }
`;

/*
 * Types
 */

interface ApolloQueryData {
    readonly viewer: Viewer;
}

/*
 * Main
 */

const ALL_YEARS = "all";

export const ReportsHomePage = (): React.ReactElement => {
    const [selectedYear, setSelectedYear] = useState<string | null>(null);
    const { data, loading, error, stopPolling } = useQuery<ApolloQueryData, Empty>(LIST_REPORTS_QUERY, {
        pollInterval: 1000,
    });

    if (loading) return <Loading />;
    if (error) return <Error />;

    if (data?.viewer.user.school?.reports.edges?.length) {
        stopPolling();
    }

    const allReports =
        data?.viewer.user.school?.reports.edges?.map((edge) => ({
            id: edge?.node?.id,
            year: edge?.node?.year,
            type: edge?.node?.type,
        })) ?? [];

    // Extract unique years (already sorted DESC from query)
    const years = Array.from(new Set(allReports.map((r) => r.year).filter(Boolean))) as string[];

    // Default to most recent year when user hasn't made a selection
    const effectiveYear = selectedYear ?? (years[0] || ALL_YEARS);

    // Filter reports by selected year
    const filteredReports =
        effectiveYear === ALL_YEARS ? allReports : allReports.filter((r) => r.year === effectiveYear);

    const yearOptions: ReadonlyArray<Choice> = [
        { id: ALL_YEARS, value: "All Years" },
        ...years.map((y) => ({ id: y, value: y })),
    ];

    const schoolName = data?.viewer.user.school?.name;

    return (
        <div className="main-page-wrap">
            <div className="center">
                {allReports.length ? (
                    <>
                        <h1>{schoolName}</h1>
                        <Separator />
                        {years.length > 1 && (
                            <div style={{ maxWidth: "240px" }}>
                                <Select
                                    id="year-filter"
                                    label="Report Year"
                                    onChange={(e) => setSelectedYear(e.target.value)}
                                    options={yearOptions}
                                    value={effectiveYear}
                                />
                            </div>
                        )}
                        <ReportGrid reports={filteredReports} />
                    </>
                ) : (
                    <Loading>
                        <p>
                            Your report is being generated...This could take a while. The page will refresh
                            automatically, you can close this page and return at any time.
                        </p>
                    </Loading>
                )}
            </div>
        </div>
    );
};
